import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAmplifyUser,
  TSignUpAmplifyDataUI2,
  TAmplifyErrorPayload,
  EErrorCode,
  TForgotPasswordUser,
  EVia,
} from 'data/types/amplify.types';
import {
  oppLocalStorage,
  oppSessionStorage,
  TStorageNames,
} from 'helpers/storage';
import { AuthAmplify } from '../apiSlice/authAmplify.slice';
import { AuthAmplifyWithEmail } from '../apiSlice/authAmplifyWithEmail.slice';
import { AuthAmplifyWithPhoneNumber } from '../apiSlice/authAmplifyWithPhoneNumber.slice';

export type TAuthSliceState = {
  amplifyUser: IAmplifyUser | null;
  signInUser: TSignUpAmplifyDataUI2 | null;
  forgotPasswordUser: TForgotPasswordUser | null;
  // The account an invitation link is signing in, while it differs from the one
  // already in session. Held here rather than read from the URL because the login
  // form strips its own query params on mount, and the route guard must keep
  // deferring to the invited account after that happens. Mirrored into
  // sessionStorage so a reload mid switch — F5, a service-worker update, a tab
  // restore — does not lose the only durable half of the signal and bounce the
  // visitor back into the unit they came from.
  invitedUsername: string | null;
  // The account that was live when that link was opened, recorded in the same
  // commit and for the same reason as `invitedUsername` — see
  // `useInvitedAccountLink`, which holds the full rationale. Named in the
  // confirmation prompt, and used to clear that account's cached tokens.
  outgoingUsername: string | null;
};

// One definition of "no switch is in progress", so the reducer that releases it
// and the sign-out matcher that wipes everything cannot drift apart.
const clearInvitedAccountSwitch = (state: TAuthSliceState) => {
  state.invitedUsername = null;
  state.outgoingUsername = null;
  oppSessionStorage.removeItem(TStorageNames.invitedUsername);
  oppSessionStorage.removeItem(TStorageNames.outgoingUsername);
};

const initialState: TAuthSliceState = {
  amplifyUser: oppLocalStorage.getItem(TStorageNames.amplifyUser),
  signInUser: null,
  forgotPasswordUser: null,
  invitedUsername: oppSessionStorage.getItem(TStorageNames.invitedUsername),
  outgoingUsername: oppSessionStorage.getItem(TStorageNames.outgoingUsername),
};

// SIGN_IN_KEEPS_INVITED_FLAG — the two signIn matchers below deliberately leave
// `invitedUsername` alone. Cognito answers a first-time login with a
// NEW_PASSWORD_REQUIRED challenge, which fulfils them while the outgoing session
// is still the live one, so releasing the flag here would hand the visitor back
// the unit they came from. The login flow releases it itself, once it has
// released that session.
const authSlice = createSlice({
  name: 'authSlice',
  initialState,
  reducers: {
    setForgotPasswordVerificationCode(
      state,
      action: PayloadAction<{ verificationCode: string }>,
    ) {
      if (!state.forgotPasswordUser) {
        return;
      }
      state.forgotPasswordUser.verificationCode =
        action.payload.verificationCode;
    },
    setAmplifyUser(state, action: PayloadAction<IAmplifyUser | null>) {
      state.amplifyUser = action.payload;
      oppLocalStorage.setItem(TStorageNames.amplifyUser, action.payload);
    },
    removeSignInUser(state) {
      state.signInUser = null;
    },
    // The two names move together or not at all — one without the other describes
    // half a switch, and every consumer reads them as a pair.
    setInvitedAccountSwitch(
      state,
      action: PayloadAction<{
        invitedUsername: string;
        outgoingUsername: string | null;
      }>,
    ) {
      const { invitedUsername, outgoingUsername } = action.payload;
      state.invitedUsername = invitedUsername;
      state.outgoingUsername = outgoingUsername;
      oppSessionStorage.setItem(TStorageNames.invitedUsername, invitedUsername);
      oppSessionStorage.setItem(
        TStorageNames.outgoingUsername,
        outgoingUsername,
      );
    },
    removeInvitedAccountSwitch(state) {
      clearInvitedAccountSwitch(state);
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        AuthAmplify.endpoints.currentAuthUserAmplify.matchFulfilled,
        (state, { payload }) => {
          state.amplifyUser = payload;
          oppLocalStorage.setItem(TStorageNames.amplifyUser, payload);
        },
      )
      .addMatcher(
        AuthAmplify.endpoints.currentAuthUserAmplify.matchRejected,
        state => {
          state.amplifyUser = null;
          oppLocalStorage.removeItem(TStorageNames.amplifyUser);
        },
      )
      .addMatcher(
        AuthAmplifyWithEmail.endpoints.signUpWithEmailAmplify.matchFulfilled,
        (state, { payload }) => {
          state.signInUser = { ...payload, via: EVia.EMAIL };
        },
      )
      .addMatcher(
        AuthAmplifyWithPhoneNumber.endpoints.signUpWithPhoneNumberAmplify
          .matchFulfilled,
        (state, { payload }) => {
          state.signInUser = { ...payload, via: EVia.PHONE };
        },
      )
      .addMatcher(
        AuthAmplifyWithEmail.endpoints.forgotPasswordWithEmailAmplify
          .matchFulfilled,
        (state, { payload }) => {
          state.forgotPasswordUser = { username: payload.username };
        },
      )
      .addMatcher(
        AuthAmplifyWithEmail.endpoints.forgotPasswordWithEmailAmplify
          .matchRejected,
        (state, { payload }) => {
          const { data } = payload as TAmplifyErrorPayload;
          if (data.code === EErrorCode.INVALID_PARAMETER_EXCEPTION) {
            state.signInUser = {
              username: data.username,
              password: '',
              userConfirmed: false,
              codeSend: false,
              via: EVia.EMAIL,
            };
          }
        },
      )
      .addMatcher(
        AuthAmplifyWithPhoneNumber.endpoints
          .forgotPasswordWithPhoneNumberAmplify.matchFulfilled,
        (state, { payload }) => {
          state.forgotPasswordUser = { username: payload.username };
        },
      )
      .addMatcher(
        AuthAmplifyWithPhoneNumber.endpoints
          .forgotPasswordWithPhoneNumberAmplify.matchRejected,
        (state, { payload }) => {
          const { data } = payload as TAmplifyErrorPayload;
          if (data.code === EErrorCode.INVALID_PARAMETER_EXCEPTION) {
            state.signInUser = {
              username: `+${data.username}`,
              password: '',
              userConfirmed: false,
              codeSend: false,
              via: EVia.PHONE,
            };
          }
        },
      )
      .addMatcher(
        AuthAmplifyWithEmail.endpoints.signInWithEmailAmplify.matchFulfilled,
        (state, { payload }) => {
          // See SIGN_IN_KEEPS_INVITED_FLAG.
          state.amplifyUser = payload;
          oppLocalStorage.setItem(TStorageNames.amplifyUser, payload);
        },
      )
      .addMatcher(
        AuthAmplifyWithEmail.endpoints.signInWithEmailAmplify.matchRejected,
        (state, { payload }) => {
          const { data } = payload as TAmplifyErrorPayload;
          if (data.code === EErrorCode.USER_NOT_CONFIRMED_EXCEPTION) {
            state.signInUser = {
              username: data.username,
              password: data.password,
              userConfirmed: false,
              codeSend: false,
              via: EVia.EMAIL,
            };
          }
        },
      )
      .addMatcher(
        AuthAmplifyWithPhoneNumber.endpoints.signInWithPhoneNumberAmplify
          .matchFulfilled,
        (state, { payload }) => {
          // See SIGN_IN_KEEPS_INVITED_FLAG.
          state.amplifyUser = payload;
          oppLocalStorage.setItem(TStorageNames.amplifyUser, payload);
        },
      )
      .addMatcher(
        AuthAmplifyWithPhoneNumber.endpoints.signInWithPhoneNumberAmplify
          .matchRejected,
        (state, { payload }) => {
          const { data } = payload as TAmplifyErrorPayload;
          if (data.code === EErrorCode.USER_NOT_CONFIRMED_EXCEPTION) {
            state.signInUser = {
              username: data.username,
              password: data.password,
              userConfirmed: false,
              codeSend: false,
              via: EVia.PHONE,
            };
          }
        },
      )
      .addMatcher(
        AuthAmplify.endpoints.signOutAmplify.matchFulfilled,
        state => {
          state.amplifyUser = null;
          clearInvitedAccountSwitch(state);
          oppLocalStorage.clear();
        },
      );
  },
});

export default authSlice;
